import { ORDER_STATUS_LABEL, formatCad, type OrderStatus } from '@nsd/core';
import { adminApi } from '@nsd/api-client';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { accessToken } from './auth.ts';

export const api = adminApi(accessToken);

/** Loads data with a reload function; one hook for every page. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await run());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [run]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, loading, reload, setData };
}

/** Wraps an action button: disables while running, shows the error if it fails. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, run };
}

export function Status({ status }: { status: OrderStatus }) {
  return <span className={`status status--${status}`}>{ORDER_STATUS_LABEL[status]}</span>;
}

export function Money({ cents }: { cents: number }) {
  return <>{formatCad(cents)}</>;
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <p className="muted">{what}...</p>;
}

export function ErrorBox({ error }: { error: string | null }) {
  return error ? <div className="notice notice--error" role="alert">{error}</div> : null;
}

export function Panel({ title, children, actions }: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="panel">
      {(title || actions) && (
        <div className="panel__head">
          {title && <h2>{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export const when = (iso: string | undefined) =>
  iso ? new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Toronto' }) : '';
